/**
 * The diet plan's free meal (refeição livre): one meal a week, on the day and at the meal the user picks in the
 * questionnaire, with a calorie budget instead of foods. The budget is that meal's share of the day's target, and the
 * day's other meals are planned with the rest, so the day still adds up to the target. Plans made before the budget
 * (29/10/2026) have a free meal with no budget: an extra meal on top of the day's target.
 */

export const FREE_MEAL_SLOTS = ['lunch', 'dinner'];
export const DEFAULT_FREE_MEAL = { day: 5, slot: 'dinner' }; // Saturday dinner

export const freeMealBudget = (targetCalories, mealsPerDay) => Math.round(targetCalories / Math.max(mealsPerDay, 1));

// Where a meal falls in the day, from its name (the AI names them like the prompt's meal types)
const MEAL_ORDER = [
  /caf[eé]|breakfast/i,
  /lanche da manh|morning snack/i,
  /almo[cç]o|lunch/i,
  /lanche da tarde|afternoon snack|lanche|snack/i,
  /jantar|dinner/i,
  /ceia|supper|evening/i,
];
const rankOf = (name = '') => {
  const rank = MEAL_ORDER.findIndex((pattern) => pattern.test(name));
  return rank === -1 ? null : rank;
};
const SLOT_RANK = { lunch: 2, dinner: 4 };

/** The meals without the first one planned for the free meal's slot (when the AI planned it anyway). */
export const withoutSlot = (meals, slot) => {
  const at = meals.findIndex((meal) => rankOf(meal.name) === SLOT_RANK[slot]);
  return at === -1 ? meals : [...meals.slice(0, at), ...meals.slice(at + 1)];
};

/** The day's meals with the free meal put in its place: after the meals that come before its slot. */
export const insertFreeMeal = (meals, { slot, name, budget }) => {
  const freeMeal = { name, foods: [], isFreeMeal: true, budget };
  const at = meals.findIndex((meal) => {
    const rank = rankOf(meal.name);
    return rank !== null && rank > SLOT_RANK[slot];
  });
  if (at === -1) return [...meals, freeMeal];
  return [...meals.slice(0, at), freeMeal, ...meals.slice(at)];
};

const ZERO = { calories: 0, protein: 0, carbs: 0, fat: 0 };

/** A meal's totals: its foods, or the free meal's calorie budget (it has no macros). */
export const mealTotals = (meal) =>
  meal.isFreeMeal
    ? { ...ZERO, calories: meal.budget || 0 }
    : (meal.foods || []).reduce(
        (acc, food) => ({
          calories: acc.calories + (food.calories || 0),
          protein: acc.protein + (food.protein || 0),
          carbs: acc.carbs + (food.carbs || 0),
          fat: acc.fat + (food.fat || 0),
        }),
        ZERO
      );

export const dayTotalsOf = (meals) =>
  meals.reduce((acc, meal) => {
    const totals = mealTotals(meal);
    return {
      calories: acc.calories + totals.calories,
      protein: acc.protein + totals.protein,
      carbs: acc.carbs + totals.carbs,
      fat: acc.fat + totals.fat,
    };
  }, ZERO);

/** Which day of a saved plan has the free meal (plans saved before the question say it only in their days). */
export const freeMealDayOf = (weekPlan) => {
  const index = (weekPlan || []).findIndex((day) => day?.meals?.some((meal) => meal.isFreeMeal));
  return index === -1 ? null : index;
};
